/**
 * View-model hook for transcript collapse state.
 *
 * Bridges the app store's collapse callbacks (TranscriptCollapseState) to the
 * event list: bulk collapse/expand, lazy default seeding on the first
 * individual toggle, and batched expansion for deep links.
 */

import { useCallback, useEffect } from "react";

import { collectAllCollapsibleIds } from "../transform/collapse";
import type { EventNode, TranscriptCollapseState } from "../types";

export interface UseTranscriptCollapseOptions {
  /** The EventNode tree (source of collapsible ids). */
  eventNodes: EventNode[];
  /** Node IDs collapsed by default. */
  defaultCollapsedIds: Record<string, true>;
  /** Collapse state and callbacks from the app store. */
  collapseState?: TranscriptCollapseState;
  /** Bulk collapse/expand of all collapsible events. Omit for no-op. */
  bulkCollapse?: "collapse" | "expand";
  /** Number of events in the sample (bulk collapse is a no-op when 0). */
  eventCount: number;
}

export interface TranscriptCollapse {
  /** Collapse/expand a single node, seeding defaults on the first toggle. */
  onCollapseTranscript: (nodeId: string, collapsed: boolean) => void;
  /** Batched expansion for deep links into collapsed regions. Undefined when
   *  the store provides no bulk setter. */
  onExpandNodes: ((nodeIds: string[]) => void) | undefined;
}

export function useTranscriptCollapse(
  options: UseTranscriptCollapseOptions
): TranscriptCollapse {
  const {
    eventNodes,
    defaultCollapsedIds,
    collapseState,
    bulkCollapse,
    eventCount,
  } = options;

  const onSetTranscriptCollapsed = collapseState?.onSetTranscriptCollapsed;
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (eventCount <= 0 || !bulkCollapse || !onSetTranscriptCollapsed) {
      return;
    }
    if (bulkCollapse === "expand") {
      onSetTranscriptCollapsed({});
    } else {
      const allCollapsibleIds = collectAllCollapsibleIds(eventNodes);
      onSetTranscriptCollapsed(allCollapsibleIds);
    }
  }, [eventNodes, bulkCollapse, onSetTranscriptCollapsed, eventCount]);

  // Lazy-seed: when the user toggles an individual node for the first time
  // (store scope is empty), seed the store with defaults before applying the
  // toggle so that all other nodes retain their default collapsed state.
  const onCollapseTranscriptRaw = collapseState?.onCollapseTranscript;
  const transcriptCollapsed = collapseState?.transcript;
  const onCollapseTranscript = useCallback(
    (nodeId: string, collapsed: boolean) => {
      if (!onCollapseTranscriptRaw || !onSetTranscriptCollapsed) return;
      if (!transcriptCollapsed) {
        // First toggle — seed defaults then apply the toggle
        onSetTranscriptCollapsed({
          ...defaultCollapsedIds,
          [nodeId]: collapsed,
        });
      } else {
        onCollapseTranscriptRaw(nodeId, collapsed);
      }
    },
    [
      onCollapseTranscriptRaw,
      onSetTranscriptCollapsed,
      transcriptCollapsed,
      defaultCollapsedIds,
    ]
  );

  // Bulk-expand for deep links into collapsed regions. One batched update —
  // sequential onCollapseTranscript calls would each re-seed defaults and
  // clobber the previous call's expansion while the store is unseeded.
  const onExpandNodes = useCallback(
    (nodeIds: string[]) => {
      if (!onSetTranscriptCollapsed) return;
      const next = { ...(transcriptCollapsed ?? defaultCollapsedIds) };
      for (const id of nodeIds) next[id] = false;
      onSetTranscriptCollapsed(next);
    },
    [onSetTranscriptCollapsed, transcriptCollapsed, defaultCollapsedIds]
  );

  return {
    onCollapseTranscript,
    onExpandNodes: onSetTranscriptCollapsed ? onExpandNodes : undefined,
  };
}
